import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Save } from '@mui/icons-material';
import materialsService from '../services/materialsService';
import api from '../services/api';
import companyService from '../services/companyService';

interface MaterialFormData {
  name: string;
  description: string;
  category: string;
  unit: string;
  unitCost: number;
  supplier: string;
  minimumStock: number;
  currentStock: number;
  location: string;
  specifications: string;
}

const MaterialCreate: React.FC = () => {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<MaterialFormData>({
    name: '',
    description: '',
    category: '',
    unit: '',
    unitCost: 0,
    supplier: '',
    minimumStock: 0,
    currentStock: 0,
    location: '',
    specifications: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[] | null>(null);

  const categories = [
    'Concrete',
    'Steel',
    'Wood',
    'Electrical',
    'Plumbing',
    'Insulation',
    'Roofing',
    'Flooring',
    'Paint',
    'Hardware',
    'Other',
  ];

  const units = [
    'pieces',
    'feet',
    'yards',
    'meters',
    'square feet',
    'square meters',
    'cubic feet',
    'cubic meters',
    'pounds',
    'kilograms',
    'tons',
    'gallons',
    'liters',
    'boxes',
    'pallets',
    'rolls',
    'sheets',
  ];

  // Fetch current user to get company ID (required by backend schema)
  React.useEffect(() => {
    // First, try to decode JWT locally to avoid network dependency
    const tryDecodeCompanyFromToken = () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const parts = token.split('.');
        if (parts.length !== 3) return;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = JSON.parse(atob(base64));
        const role = json?.user?.role ?? json?.role;
        if (role) setUserRole(String(role));
        const companyRaw = json?.user?.company ?? json?.company;
        const id = typeof companyRaw === 'object' ? (companyRaw?._id || companyRaw?.id) : companyRaw;
        if (id) {
          setCompanyId(String(id));
          setSelectedCompanyId(String(id));
        }
      } catch (e) {
        // Ignore decode errors and fall back to /auth/me
      }
    };

    const bootstrap = async () => {
      try {
        // Try local decode first for a faster path
        tryDecodeCompanyFromToken();

        const res = await api.get('/auth/me');
        const user = res.data?.user || res.data;
        const company = user?.company;
        const role = user?.role;
        if (role) setUserRole(String(role));
        const id = typeof company === 'object' ? (company?._id || company?.id) : company;
        if (id) {
          setCompanyId(String(id));
          setSelectedCompanyId(String(id));
        }
      } catch (err) {
        // Non-blocking; backend may reject without company
        console.warn('Unable to fetch current user/company for material create');
      }
    };
    bootstrap();
  }, []);

  // Load companies when we know the user is admin
  React.useEffect(() => {
    const loadCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const items = await companyService.getAllCompanies();
        const opts = items.map((c: any) => ({ id: String(c.id), name: c.name }));
        setCompanies(opts);
        if (!selectedCompanyId && opts.length > 0) {
          setSelectedCompanyId(opts[0].id);
        }
      } catch (e) {
        console.warn('Unable to load companies for selection', e);
      } finally {
        setCompaniesLoading(false);
      }
    };
    if (userRole === 'admin') {
      loadCompanies();
    }
  }, [userRole]);

  const handleInputChange = (field: keyof MaterialFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'unitCost' || field === 'minimumStock' || field === 'currentStock' 
        ? parseFloat(value) || 0 
        : value,
    }));
  };

  const handleSelectChange = (field: keyof MaterialFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setErrorDetails(null);

    // Determine effective company id (admin may select)
    const effectiveCompanyId = userRole === 'admin' ? (selectedCompanyId || companyId) : companyId;

    // Prevent submit without a company, since backend requires it
    if (!effectiveCompanyId) {
      setLoading(false);
      setError('Validation error');
      setErrorDetails([
        userRole === 'admin'
          ? 'Path `company` is required. Please select a company.'
          : 'Path `company` is required. Please log in again or ensure your account is associated with a company.'
      ]);
      return;
    }

    try {
      // Map form data to backend schema (Material.js)
      const payload: any = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        unit: formData.unit,
        currentStock: formData.currentStock,
        minimumStock: formData.minimumStock,
        // Nested pricing object with required unitCost
        pricing: {
          unitCost: formData.unitCost,
        },
        // Include company from authenticated user
        company: effectiveCompanyId,
        // Map simple location string into structured warehouse field
        ...(formData.location
          ? { location: { warehouse: formData.location } }
          : {}),
      };

      await materialsService.create(payload);
      navigate('/materials');
    } catch (err) {
      const axiosErr: any = err;
      const data = axiosErr?.response?.data;
      const message = (data?.message as string) || (axiosErr?.message as string) || 'Failed to create material';
      const details: string[] = Array.isArray(data?.errors)
        ? data.errors
        : (data?.error ? [String(data.error)] : []);
      setError(message);
      setErrorDetails(details.length ? details : null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/materials')}
          sx={{ textDecoration: 'none' }}
        >
          Materials
        </Link>
        <Typography color="text.primary">Create Material</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/materials')}
          sx={{ mr: 2 }}
        >
          Back to Materials
        </Button>
        <Typography variant="h4" component="h1">
          Create New Material
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <div>{error}</div>
          {errorDetails && errorDetails.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {errorDetails.map((d, idx) => (
                <li key={idx}>{d}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Material Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                />
          </Grid>
          {userRole === 'admin' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedCompanyId || ''}
                  onChange={(e: any) => setSelectedCompanyId(e.target.value)}
                  label="Company"
                  disabled={companiesLoading}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={handleSelectChange('category')}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange('description')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={formData.unit}
                    onChange={handleSelectChange('unit')}
                    label="Unit"
                  >
                    {units.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Cost ($)"
                  type="number"
                  value={formData.unitCost}
                  onChange={handleInputChange('unitCost')}
                  inputProps={{ min: 0, step: 0.01 }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Supplier"
                  value={formData.supplier}
                  onChange={handleInputChange('supplier')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Minimum Stock"
                  type="number"
                  value={formData.minimumStock}
                  onChange={handleInputChange('minimumStock')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Current Stock"
                  type="number"
                  value={formData.currentStock}
                  onChange={handleInputChange('currentStock')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Specifications"
                  value={formData.specifications}
                  onChange={handleInputChange('specifications')}
                  multiline
                  rows={4}
                  placeholder="Enter detailed specifications, dimensions, quality standards, etc."
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Material'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/materials')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MaterialCreate;